/**
 * Sign-up v3 — the rules behind `/onboard` (design_handoff_signup_v3, Oct 22 2026; v3.1 Oct 30).
 *
 * People first, profile last. Six screens: ENTER (`/register`) → KNOW ANYONE HERE? → CONTACTS →
 * WHAT ARE YOU HERE TO FIND? → THE BASICS → the square, offering "Make an introduction." when a
 * photo went up. Screens 2–5 are this page's four steps. Profile depth (music, strengths,
 * offers, privacy, handle) moved entirely to the Portal Editor (`/portal/edit`). The YOU strip on
 * screen 2 (Oct 22 2026, handoff rev 2) asks the name — required, because the friend requests and
 * the invite text carry it — plus WHAT YOU DO and the Instagram / TikTok handles, all optional.
 *
 * WHAT is asked — the chip vocabulary, the caps — stays in `utils/portalPageModel`, shared with the
 * editor. This file holds only what is specific to walking the steps: the titles, what GO LIVE
 * still needs, where a half-finished account resumes, and the PUT body.
 */
import { MAX_SMS_RECIPIENTS } from './smsCompose';
import { HERE_TO_FIND, LOOKING_MAX, OCCUPATION_MAX, VIBE_MAX, decodeExtended, instagramHandle, normalizeInstagram, socialHandle } from './portalPageModel';

/** Screen number → the top bar's title. Screen 1 is `/register`, screen 6 the City Portal. */
export const STEP_TITLES = { 2: 'KNOW ANYONE?', 3: 'CONTACTS', 4: 'ABOUT YOU', 5: 'THE BASICS' };
export const FIRST_SCREEN = 2;
export const LAST_SCREEN = 5;
export const STEP_COUNT = 4;

/** `STEP n OF 4` — the position of a screen in the four. */
export const stepNumber = (screen) => screen - 1;

/** Headline + subhead per screen. `{n}` on screen 3 is the match count. */
export const STEP_HEADS = {
  2: ['Know anyone here?', 'Tap the ones you know. They become your first friends.'],
  3: ['{n} of your contacts are here.', 'Add them in one tap, then pick who should be here too.'],
  4: ['What are you here to find?', 'Pick any. Portal uses them to introduce you. Change them any time.'],
  5: ['The basics.', ''],
};

/** The CONTACTS headline for a match count. */
export function contactsHeadline(matchCount, source) {
  if (source === 'none') return 'Who should be here?';
  if (!matchCount) return 'None of your contacts are here yet.';
  if (matchCount === 1) return '1 of your contacts is here.';
  return `${matchCount} of your contacts are here.`;
}

/** How many people one CONTACTS step may invite by text — the same ceiling as INVITE FRIENDS. */
export const INVITE_CAP = MAX_SMS_RECIPIENTS;

/**
 * What screen 2's SYNC CONTACTS is still missing: the name, because the friend requests it sends
 * and the text on screen 3 both carry it. The blocker line is `STEP2_BLOCKER`.
 */
export function step2Missing(form) {
  return form.name.trim() ? [] : ['your name'];
}
export const STEP2_BLOCKER = 'Still needed: your name, so the texts have one on them.';

/**
 * What the last step is still missing, as the phrases the blocker line joins. This is THE rule —
 * the footer's pressable state, the pink rings and the resume logic all read it. Only the name
 * blocks (v3.1, Oct 29 2026): the photo is optional, and `goLiveLabel` says which way it went.
 */
export function step5Missing(form) {
  return form.name.trim() ? [] : ['your name'];
}

/** GO LIVE with a face; without one the person still goes in, and no introduction is ever offered. */
export const goLiveLabel = (form) => (form.photo ? 'GO LIVE →' : 'GO IN WITHOUT A PHOTO →');

/** This browser session finished sign-up. Holds the resume prompt back until the next visit. */
const JOINED_KEY = 'portalJustJoined';
export function markJustJoined() {
  try { sessionStorage.setItem(JOINED_KEY, '1'); } catch { /* storage off */ }
}
export function justJoined() {
  try { return sessionStorage.getItem(JOINED_KEY) === '1'; } catch { return false; }
}

/** Does this profile satisfy the photo rule? The profile photo gate asks exactly this. */
export const hasProfilePhoto = (profile) =>
  !!(profile?.avatarUrl || (Array.isArray(profile?.galleryPhotos) && profile.galleryPhotos.length > 0));

/**
 * Where a half-finished account resumes. `intake_current_step` is still the old 1–8 watermark —
 * a `Range(1, 8)` column with live rows in it — so v3 keeps writing that scale and reads it back
 * through this map: `1,2 → 2 · 3,4 → 3 · 5,6,7 → 4 · 8 → 5`.
 */
export function resumeStep(intakeCurrentStep) {
  const n = Number.isInteger(intakeCurrentStep) ? intakeCurrentStep : 1;
  if (n >= 8) return 5;
  if (n >= 5) return 4;
  if (n >= 3) return 3;
  return 2;
}

/**
 * The watermark to write when the person lands on screen `next` — the inverse of `resumeStep`,
 * so leaving and coming back lands on the screen they were on. (`resumeStep(intakeStepFor(n)) === n`.)
 */
export const intakeStepFor = (next) => ({ 2: 1, 3: 3, 4: 5, 5: 8 }[next] || 1);

/** An empty form. */
export const emptyForm = () => ({
  tapped: [],          // screen 2: user ids tapped
  job: '',             // screen 2: WHAT YOU DO (extendedProfile.occupation)
  ig: '',              // screen 2: Instagram handle (the profile's socialLink)
  tt: '',              // screen 2: TikTok handle (extendedProfile.tiktok)
  addedAll: false,     // screen 3: ADD ALL pressed
  invites: [],         // screen 3: contact ids picked to text (≤ INVITE_CAP)
  finds: [],           // screen 4: HERE TO FIND picks (vocabulary + custom)
  custom: [],          // screen 4: chips written in
  looking: '',         // screen 4: RIGHT NOW I'M LOOKING FOR
  photo: null,         // screen 5: { id, url }
  name: '',
  vibe: '',
});

/**
 * A ProfileDto → the form. Returning to the flow shows what is already saved, so a second pass
 * never asks twice. The name falls back to what the sign-in left behind (`onboardingDisplayName`
 * / `googleSuggestedName`); a registration's default name (the email's local part) is treated as
 * blank, because "brenden.leib" is not what people call anyone.
 */
export function formFromProfile(profile, savedName = '') {
  const base = emptyForm();
  if (!profile) return { ...base, name: savedName };
  const ext = decodeExtended(profile.extendedProfile);
  const finds = Array.isArray(ext.hereToFind) ? ext.hereToFind.filter((s) => typeof s === 'string' && s.trim()) : [];
  const vocab = new Set(HERE_TO_FIND.map((c) => c.toLowerCase()));
  const lead = (Array.isArray(profile.galleryPhotos) ? profile.galleryPhotos : []).find((p) => p && p.url);
  const name = profile.displayName || '';
  return {
    ...base,
    finds,
    custom: finds.filter((c) => !vocab.has(c.toLowerCase())),
    looking: (typeof ext.lookingFor === 'string' ? ext.lookingFor : '').slice(0, LOOKING_MAX),
    job: (typeof ext.occupation === 'string' ? ext.occupation : '').trim().slice(0, OCCUPATION_MAX),
    ig: instagramHandle(profile.socialLink),
    tt: socialHandle(ext.tiktok),
    photo: profile.avatarUrl ? { id: String(ext.avatarPhotoId || lead?.id || 'avatar'), url: profile.avatarUrl } : lead ? { id: String(lead.id), url: lead.url } : null,
    name: looksLikeEmailLocalPart(name, profile) ? (savedName || '') : (name || savedName),
    vibe: (profile.vibeCheck || '').replace(/^["“]|["”]$/g, '').slice(0, VIBE_MAX),
  };
}

/** The registration seed is the email's local part; it has never been typed by the person. */
function looksLikeEmailLocalPart(name, profile) {
  if (!name) return false;
  if (profile?.intakeCompletedAt) return false;
  return /^[a-z0-9][a-z0-9._+-]*$/.test(name) && /[._+\d-]/.test(name) && !/\s/.test(name);
}

/**
 * The PUT /profiles/me body. Every step sends the whole form, so a screen that is skipped or
 * revisited can never half-write. `extendedProfile` merges into whatever the server holds so the
 * editor's own keys (`coverLayout`, `sections`, `photoFocus`, `avatarPhotoId`, `strengths`, …)
 * survive a return trip through here. `screen` is where the person is landing; it rides along as
 * the old 1–8 watermark. `city` fills a blank location (the town the ENTER screen showed).
 */
export function buildOnboardingPayload(form, profile, screen, { city = null, goLive = false } = {}) {
  const ext = {
    ...decodeExtended(profile?.extendedProfile),
    hereToFind: form.finds,
    lookingFor: form.looking.trim() || null,
    occupation: form.job.trim().slice(0, OCCUPATION_MAX) || null,
    tiktok: socialHandle(form.tt) || null,
  };
  const body = {
    extendedProfile: JSON.stringify(ext),
    intakeCurrentStep: goLive ? 8 : intakeStepFor(screen),
  };
  if (form.name.trim()) body.displayName = form.name.trim();
  // The Instagram handle is the profile's own column. Only written when typed: the API ignores a
  // null here anyway, and a blank on sign-up is the common case, not a request to clear.
  if (socialHandle(form.ig)) body.socialLink = normalizeInstagram(socialHandle(form.ig));
  if (form.vibe.trim()) body.vibeCheck = form.vibe.trim();
  else if (goLive) body.vibeCheck = null;
  if (city && !(profile?.location || '').trim()) body.location = city;
  if (goLive && !profile?.termsAcceptedAt) body.acceptTerms = true;
  return body;
}
